import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  NavItem,
  NavLink,
  Nav,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import NotificationAlert from "react-notification-alert";
import { format } from "date-fns";
import jsPDF from "jspdf";
import htmlToPdfmake from "html-to-pdfmake";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import { AsistenciaPresencialService } from "service/AsistenciaPresencialService";
import { CicloService } from "service/CicloService";
import { SalonService } from "service/SalonService";
import ModalSubirAsistencia from "components/Modals/ModalSubirAsistencia";
import FSelect from "components/Select/FSelect";
import FDateInput from "components/DateInput/FDateInput";

if (pdfFonts && pdfFonts.pdfMake) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
} else if (pdfFonts) {
  pdfMake.vfs = pdfFonts.vfs;
}

function AsistenciaPresencialPage() {
  const [activeTab, setActiveTab] = useState("1");
  const [listCiclos, setListCiclos] = useState([]);
  const [listSalones, setListSalones] = useState([]);
  const [listReporte, setListReporte] = useState([]);
  
  const [filters, setFilters] = useState({
    _idciclo: localStorage.getItem("_c") || "",
    _idsalon: localStorage.getItem("_s") || "0",
    turnoasistencia: new Date().getHours() >= 14 ? "Tarde" : "Maniana",
    fechadesde: format(new Date(), "yyyy-MM-dd"),
    fechahasta: format(new Date(), "yyyy-MM-dd"),
    turnoReporte: "Todos"
  });

  const [alumno, setAlumno] = useState({
    documentoidentidad: "",
    nombres: "",
  });

  const [modalImportOpen, setModalImportOpen] = useState(false);

  const notificationAlertRef = useRef(null);
  const pdfTableRef = useRef(null);

  const asistenciaService = new AsistenciaPresencialService();
  const cicloService = new CicloService();
  const salonService = new SalonService();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const resCiclos = await cicloService.getCiclos();
      const resSalones = await salonService.getSalones();
      
      const ciclos = resCiclos?.data || [];
      const salones = resSalones?.data || [];
      
      setListCiclos(ciclos);
      setListSalones(salones);

      if (!filters._idciclo && ciclos.length > 0) {
        setFilters(prev => ({ ...prev, _idciclo: ciclos[0]._id }));
      }
    } catch (error) {
      console.error("Error cargando datos iniciales:", error);
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tc",
      message: (
        <div>
          <div>{message}</div>
        </div>
      ),
      type: type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 7,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleModalImport = () => {
    setModalImportOpen(!modalImportOpen);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    if (name === "_idciclo") localStorage.setItem("_c", value);
    if (name === "_idsalon") localStorage.setItem("_s", value);
  };

  const handleDniChange = async (e) => {
    const value = e.target.value;
    setAlumno({ ...alumno, documentoidentidad: value });

    if (value.length === 8) {
      try {
        const dto = {
          documentoidentidad: value,
          ciclo: filters._idciclo,
          tipoasistencia: filters.turnoasistencia,
        };
        const response = await asistenciaService.addAsistenciaPresencial(dto);
        
        if (response.confirmation === "success") {
          const data = response.data?.data || response.data;
          const nombresCompletos = data?.alumno 
            ? `${data.alumno.nombres} ${data.alumno.apellidopaterno} ${data.alumno.apellidomaterno}`
            : "Registrado";
            
          setAlumno({
            documentoidentidad: "",
            nombres: nombresCompletos,
          });
          notify("success", "ASISTENCIA REGISTRADA!");
        } else {
          notify("warning", response.message || "Error al registrar asistencia.");
          setAlumno({ ...alumno, documentoidentidad: "" });
        }
      } catch (error) {
        notify("danger", "Ocurrió un error al procesar la asistencia.");
        console.error(error);
      }
    }
  };

  const getReporte = async () => {
    try {
      const dto = {
        ciclo: filters._idciclo,
        fechadesde: filters.fechadesde,
        fechahasta: filters.fechahasta,
        salon: filters._idsalon,
      };

      if (filters.turnoReporte === "Maniana") {
        dto.asistiomaniana = "true";
      } else if (filters.turnoReporte === "Tarde") {
        dto.asistiotarde = "true";
      } else {
        dto.tipoasistencia = "Todos";
      }

      const response = await asistenciaService.getReporte(dto);
      
      // Extraer los datos del reporte manejando diferentes estructuras de respuesta
      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (Array.isArray(response?.data)) {
        data = response.data;
      } else if (Array.isArray(response?.data?.data)) {
        data = response.data.data;
      } else if (response?.data && typeof response.data === 'object') {
        // En caso de que sea un objeto que no es un arreglo pero es el que contiene la información
        data = [response.data];
      }

      // Ordenar por apellido paterno
      data.sort((a, b) => 
        (a.alumno?.apellidopaterno || "").localeCompare(b.alumno?.apellidopaterno || "")
      );
      setListReporte(data);
    } catch (error) {
      console.error("Error obteniendo reporte:", error);
      notify("danger", "Error al obtener el reporte.");
    }
  };

  const downloadPDF = () => {
    if (listReporte.length === 0) {
      notify("warning", "No hay datos para exportar.");
      return;
    }

    const pdfTable = pdfTableRef.current;
    const html = htmlToPdfmake(pdfTable.innerHTML);
    const docDefinition = {
      content: html,
      styles: {
        "table-bordered": {
          margin: [0, 5, 0, 15]
        }
      }
    };
    pdfMake.createPdf(docDefinition).download(`Reporte_Asistencia_${filters.fechadesde}.pdf`);
  };

  const cleanInputs = () => {
    setAlumno({
      documentoidentidad: "",
      nombres: "",
    });
  };

  return (
    <>
      <div className="content">
        <NotificationAlert ref={notificationAlertRef} />
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Asistencia Presencial</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 3: Barra de acciones global (opcional, movemos Importar aquí) */}
                <Row className="mb-4">
                  <Col md="6"></Col>
                  <Col md="6" className="text-right">
                    <Button color="info" className="btn-fill" onClick={toggleModalImport}>
                      <i className="tim-icons icon-upload mr-2" />
                      Importar desde Excel
                    </Button>
                  </Col>
                </Row>

                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggleTab("1")}
                      style={{ cursor: "pointer" }}
                    >
                      Registrar Asistencia
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggleTab("2")}
                      style={{ cursor: "pointer" }}
                    >
                      Reporte
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    {/* Zona 2 para Tab 1 */}
                    <Row className="align-items-end mb-4 mt-2">
                      <Col md="5">
                        <FormGroup className="mb-0">
                          <Label for="_idciclo">Ciclo Académico</Label>
                          <FSelect
                            id="_idciclo"
                            name="_idciclo"
                            value={filters._idciclo}
                            onChange={handleInputChange}
                            options={listCiclos.map((c) => ({ value: c._id, label: c.denominacion }))}
                            placeholder="Seleccione Ciclo"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-0">
                          <Label>Tipo de Asistencia (Turno)</Label>
                          <div className="d-flex align-items-center" style={{ height: "40px" }}>
                            <FormGroup check inline className="mb-0">
                              <Label check>
                                <Input
                                  type="radio"
                                  name="turnoasistencia"
                                  value="Maniana"
                                  checked={filters.turnoasistencia === "Maniana"}
                                  onChange={handleInputChange}
                                />{" "}
                                Mañana
                              </Label>
                            </FormGroup>
                            <FormGroup check inline className="mb-0">
                              <Label check>
                                <Input
                                  type="radio"
                                  name="turnoasistencia"
                                  value="Tarde"
                                  checked={filters.turnoasistencia === "Tarde"}
                                  onChange={handleInputChange}
                                />{" "}
                                Tarde
                              </Label>
                            </FormGroup>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    
                    <hr className="my-4" />
                    
                    <Row className="align-items-end">
                      <Col md="6">
                        <FormGroup>
                          <Label for="dni">Documento de Identidad (8 dígitos)</Label>
                          <Input
                            type="text"
                            id="dni"
                            placeholder="Ingrese DNI para registrar"
                            value={alumno.documentoidentidad}
                            onChange={handleDniChange}
                            maxLength="8"
                            autoFocus
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label for="nombres">Alumno Detectado</Label>
                          <Input
                            type="text"
                            id="nombres"
                            value={alumno.nombres}
                            disabled
                            placeholder="Nombre del alumno registrado"
                            style={{ height: "40px" }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="text-right">
                        <Button color="secondary" size="sm" onClick={cleanInputs}>
                          Limpiar
                        </Button>
                      </Col>
                    </Row>
                  </TabPane>
                  <TabPane tabId="2">
                    {/* Zona 2 para Tab 2 */}
                    <Row className="align-items-end mb-4 mt-2">
                      <Col md="2">
                        <FormGroup className="mb-0">
                          <Label for="turnoReporte">Turno</Label>
                          <FSelect
                            id="turnoReporte"
                            name="turnoReporte"
                            value={filters.turnoReporte}
                            onChange={handleInputChange}
                            options={[
                              { value: "Maniana", label: "Mañana" },
                              { value: "Tarde", label: "Tarde" },
                              { value: "Todos", label: "Todos" },
                            ]}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup className="mb-0">
                          <Label for="_idsalon">Salón</Label>
                          <FSelect
                            id="_idsalon"
                            name="_idsalon"
                            value={filters._idsalon}
                            onChange={handleInputChange}
                            options={[
                              { value: "0", label: "TODOS LOS SALONES" },
                              ...listSalones.map((s) => ({ value: s._id, label: s.denominacion })),
                            ]}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup className="mb-0">
                          <Label for="fechadesde">Desde</Label>
                          <FDateInput
                            name="fechadesde"
                            id="fechadesde"
                            value={filters.fechadesde}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup className="mb-0">
                          <Label for="fechahasta">Hasta</Label>
                          <FDateInput
                            name="fechahasta"
                            id="fechahasta"
                            value={filters.fechahasta}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="1">
                        <FormGroup className="mb-0">
                          <Button
                            color="info"
                            block
                            onClick={getReporte}
                            style={{ height: "40px", padding: "0" }}
                            title="Consultar"
                          >
                            <i className="tim-icons icon-zoom-split" />
                          </Button>
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Zona 3 para Tab 2 */}
                    <Row className="mb-4">
                      <Col md="6"></Col>
                      <Col md="6" className="text-right">
                        {listReporte.length > 0 && (
                          <Button color="success" className="btn-fill" onClick={downloadPDF}>
                            <i className="tim-icons icon-cloud-download-93 mr-2" />
                            Descargar PDF
                          </Button>
                        )}
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <div ref={pdfTableRef}>
                      <div className="text-center d-none d-print-block">
                        <h4>Resumen de Asistencias</h4>
                        <p>Ciclo: {listCiclos.find(c => c._id === filters._idciclo)?.denominacion || ""}</p>
                        <p>Salón: {filters._idsalon === "0" ? "TODOS" : listSalones.find(s => s._id === filters._idsalon)?.denominacion || ""}</p>
                      </div>
                      <Table responsive>
                        <thead className="text-primary">
                          <tr>
                            <th>N°</th>
                            <th>DNI</th>
                            <th>Apellidos y Nombres</th>
                            <th>Mañana</th>
                            <th>Tarde</th>
                            <th>Salón</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listReporte.map((r, index) => (
                            <tr key={r._id || index}>
                              <td>{index + 1}</td>
                              <td>{r.documentoidentidad}</td>
                              <td>
                                {`${r.alumno?.apellidopaterno || ""} ${r.alumno?.apellidomaterno || ""}, ${r.alumno?.nombres || ""}`}
                              </td>
                              <td>
                                {r.horaingresomaniana ? format(new Date(r.horaingresomaniana), "dd/MM/yyyy HH:mm:ss") : "-"}
                              </td>
                              <td>
                                {r.horaingresotarde ? format(new Date(r.horaingresotarde), "dd/MM/yyyy HH:mm:ss") : "-"}
                              </td>
                              <td>{r.salon?.denominacion || "-"}</td>
                            </tr>
                          ))}
                          {listReporte.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center">No hay registros para mostrar</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ModalSubirAsistencia 
        isOpen={modalImportOpen} 
        toggle={toggleModalImport} 
        notify={notify}
        data={{
          ciclo: filters._idciclo,
          denominacionciclo: listCiclos.find(c => c._id === filters._idciclo)?.denominacion || "",
          tipoasistencia: filters.turnoasistencia
        }}
      />
    </>
  );
}

export default AsistenciaPresencialPage;
